import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { nativeStandbyPermission, useDeviceAdminResponses } from './nativeStandbyPermission';
import { POWER_METHOD_OPTIONS as METHOD_OPTIONS, powerMethodLabel as methodLabel } from './powerMethods';

const MAX_POWER_METHODS_PER_PHASE = 12;
const observationTime = (value) => typeof value === 'number' ? value : Date.parse(value);

const parseObject = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return {}; }
};

export function normalizeMac(value) {
  const compact = String(value || '').replace(/[^0-9a-f]/gi, '').toUpperCase();
  if (!/^[0-9A-F]{12}$/.test(compact)) return '';
  return compact.match(/.{2}/g).join(':');
}

export function deviceMac(device) {
  const integration = parseObject(device?.integration_data || device?.integrationData);
  const controls = parseObject(device?.tv_controls || device?.tvControls || integration.tv_controls || integration.tvControls);
  return [
    device?.mac, controls.mac, integration.mac, integration.appNode?.mac,
    integration.remoteTarget?.mac, integration.appNode?.wifiMac,
  ].map(normalizeMac).find((value) => value && ![
    '00:00:00:00:00:00', 'FF:FF:FF:FF:FF:FF', '02:00:00:00:00:00',
  ].includes(value)) || '';
}

function deviceBridgeCapabilities(device) {
  const integration = parseObject(device?.integration_data || device?.integrationData);
  return integration.appNode?.bridgeCapabilities || {};
}

export function availablePowerMethods(screen, devices, phase) {
  const byId = new Map(devices.map((device) => [Number(device.id), device]));
  const television = byId.get(Number(screen?.televisionDeviceId));
  const playerIds = (screen?.players || []).map((entry) => Number(entry.deviceId || entry.id)).filter(Boolean);
  const playerId = Number(screen?.preferredPlayerDeviceId || playerIds[0]);
  const players = playerIds.map((id) => byId.get(id)).filter(Boolean);
  return (METHOD_OPTIONS[phase] || []).filter((option) => {
    if (option.role === 'player' && !playerId && option.type !== 'native_app_standby') return false;
    if (option.type === 'native_app_standby' && ![television, ...players].filter(Boolean).some((device) => deviceBridgeCapabilities(device).nativeStandbySetup === true && nativeStandbyPermission(device) !== 'unsupported')) return false;
    if (option.type === 'wol' && !television && !players.length) return false;
    if (option.type === 'tv_input_then_player_home_cec') {
      return Boolean(television && playerId && (screen?.players || [])
        .some((entry) => /^HDMI\s*[1-8]$/i.test(entry.expectedInput || '')));
    }
    return screen?.displayKind !== 'unmanaged'
      || option.role === 'player'
      || option.type === 'manual'
      || Boolean(playerId);
  });
}

export function powerTargetOptions(screen, devices, methodType) {
  const byId = new Map(devices.map((device) => [Number(device.id), device]));
  const targets = [];
  const televisionId = Number(screen?.televisionDeviceId);
  if (televisionId && !['player_home_cec', 'tv_input_then_player_home_cec'].includes(methodType)) {
    const television = byId.get(televisionId);
    targets.push({
      id: televisionId,
      role: 'television',
      label: `TV — ${television?.name || screen?.televisionName || `Device ${televisionId}`}`,
    });
  }
  (screen?.players || []).forEach((entry) => {
    const id = Number(entry.deviceId || entry.id);
    if (!id || targets.some((target) => target.id === id)) return;
    const device = byId.get(id);
    const preferred = id === Number(screen?.preferredPlayerDeviceId);
    targets.push({
      id,
      role: 'player',
      label: `${preferred ? 'Preferred player' : 'Player'} — ${device?.name || entry.name || `Device ${id}`}`,
    });
  });
  if (methodType === 'tv_input_then_player_home_cec') {
    return targets.filter((target) => {
      const player = screen?.players?.find((entry) => Number(entry.deviceId || entry.id) === target.id);
      return /^HDMI\s*[1-8]$/i.test(player?.expectedInput || '');
    });
  }
  if (methodType === 'native_app_standby') {
    return targets.filter((target) => deviceBridgeCapabilities(byId.get(target.id)).nativeStandbySetup === true && nativeStandbyPermission(byId.get(target.id)) !== 'unsupported');
  }
  return targets;
}

export function powerEffectSummary(method, phase) {
  const effects = method?.effects || {};
  const parts = [];
  if (effects.displayPower === 'yes') parts.push(phase === 'startup' ? 'TV wakes' : 'TV turns off');
  if (effects.playerPower === 'yes') parts.push(phase === 'startup' ? 'player wakes' : 'player also turns off');
  if (effects.selectsInput === 'yes') parts.push('correct input selected');
  if (effects.cecObserved === 'yes') parts.push('CEC confirmed');
  return parts.join(' · ') || 'physical effects not confirmed';
}

export function preferredPowerTargetId(options, focusedDeviceId, option, screen) {
  const focusedId = Number(focusedDeviceId);
  if (options.some((entry) => entry.id === focusedId)) return focusedId;
  return option?.role === 'player'
    ? Number(screen?.preferredPlayerDeviceId || options.find((entry) => entry.role === 'player')?.id)
    : Number(screen?.televisionDeviceId || options[0]?.id);
}

export function powerMethodTargetId(method, screen) {
  return Number(method?.targetDeviceId || (
    method?.targetRole === 'player' ? screen?.preferredPlayerDeviceId : screen?.televisionDeviceId
  )) || null;
}

export function isPowerMethodConfigured(methods, type) {
  return methods.some((method) => method.type === type && method.verification?.status === 'confirmed');
}

// Only an explicit save applies this preference; never silently reorder tested routines.
export function preferConfirmedPowerMethod(methods, phase) {
  const preferences = phase === 'startup' ? ['wol', 'android_tv_wake'] : ['android_tv_standby', 'android_tv_power_toggle'];
  const preferred = preferences.find((type) => methods.some((method) => method.type === type
    && method.enabled !== false && method.verification?.status === 'confirmed'));
  const promoted = (method) => method.type === preferred && method.enabled !== false
    && method.verification?.status === 'confirmed';
  return [...methods.filter(promoted), ...methods.filter((method) => !promoted(method))];
}

const emptyProfile = () => ({
  version: 1,
  startup: { methods: [] },
  shutdown: { methods: [] },
});

function PowerOnboarding({ areaId, topology, devices, onTopologyChange, selectedScreenId = null, selectedPhase = null, selectedDeviceId = null, tvMethods = false, compact = false, onBusyChange, observationTimeoutMs = 15000 }) {
  useDeviceAdminResponses();
  const screens = useMemo(() => (topology?.screens || []).filter((screen) => screen.id), [topology]);
  const [screenId, setScreenId] = useState('');
  const activeProbe = useRef(0);
  useEffect(() => () => { activeProbe.current += 1; }, []);
  const [focusedDeviceId, setFocusedDeviceId] = useState('');
  const [localPhase, setPhase] = useState('startup');
  const phase = selectedPhase || localPhase;
  const [methodType, setMethodType] = useState('');
  const [targetRole, setTargetRole] = useState('television');
  const [targetDeviceId, setTargetDeviceId] = useState('');
  const [mac, setMac] = useState('');
  const [macEdited, setMacEdited] = useState(false);
  const [query, setQuery] = useState('');
  const [instructions, setInstructions] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [probe, setProbe] = useState(null);
  const [observations, setObservations] = useState({ displayPower: null, playerPower: null, selectsInput: null, cecObserved: null });
  const [observationSource, setObservationSource] = useState('physical');
  const [observationNotes, setObservationNotes] = useState('');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const activeScreenId = selectedScreenId || screenId;
  const screen = screens.find((entry) => Number(entry.id) === Number(activeScreenId)) || screens[0] || null;
  const profile = screen?.activationStrategy?.powerProfile || emptyProfile();
  const methods = profile?.[phase]?.methods || [];
  const available = availablePowerMethods(screen, devices, phase);
  const targetOptions = powerTargetOptions(screen, devices, methodType).filter((target) => !selectedDeviceId || tvMethods || target.id === Number(selectedDeviceId));
  const belongsToDevice = (method) => !selectedDeviceId || powerMethodTargetId(method, screen) === Number(selectedDeviceId)
    || (tvMethods && (method.effects?.displayPower === 'yes' || ['player_home_cec', 'tv_input_then_player_home_cec'].includes(method.type)));

  useEffect(() => { onBusyChange?.(busy); }, [busy, onBusyChange]);

  useEffect(() => {
    if ((!screenId || selectedScreenId) && screen) {
      setScreenId(String(screen.id));
      setFocusedDeviceId(String(selectedDeviceId || screen.televisionDeviceId || screen.preferredPlayerDeviceId || ''));
    }
  }, [screen, screenId, selectedScreenId, selectedDeviceId]);

  useEffect(() => {
    activeProbe.current += 1;
    setBusy(false);
    setProbe(null);
    setMessage('');
    setMethodType('');
    setTargetDeviceId('');
  }, [phase, screen?.id, selectedDeviceId]);

  const knownMac = deviceMac(devices.find((device) => Number(device.id) === Number(targetDeviceId)));
  useEffect(() => {
    // Site Control metadata may arrive after the user has selected WOL.
    if (methodType === 'wol' && !macEdited && knownMac) setMac(knownMac);
  }, [methodType, targetDeviceId, knownMac, macEdited]);

  const replaceProfile = (nextProfile) => ({
    ...topology,
    screens: topology.screens.map((entry) => Number(entry.id) !== Number(screen.id) ? entry : ({
      ...entry,
      activationStrategy: { ...(entry.activationStrategy || {}), powerProfile: nextProfile },
    })),
  });

  const saveProfile = async (nextProfile) => {
    const response = await axios.put(
      `/api/areas/${areaId}/topology/screens/${screen.id}/power-profile`,
      { powerProfile: nextProfile },
      { headers }
    );
    const nextTopology = response.data.topology || replaceProfile(response.data.powerProfile);
    onTopologyChange?.(nextTopology);
    return nextTopology;
  };

  const updateMethods = async (nextMethods) => {
    setBusy(true);
    setMessage('');
    try {
      await saveProfile({ ...emptyProfile(), ...profile, [phase]: { methods: nextMethods } });
      setMessage('Power method order saved.');
    } catch (error) {
      setMessage(error.response?.data?.message || error.message);
    } finally {
      setBusy(false);
    }
  };

  const addMethod = async () => {
    const option = available.find((entry) => entry.type === methodType);
    if (!option || methods.length >= MAX_POWER_METHODS_PER_PHASE) return;
    const selectedTarget = targetOptions.find((entry) => Number(entry.id) === Number(targetDeviceId));
    if (!selectedTarget) return;
    const role = selectedTarget.role;
    const selectedTargetDeviceId = selectedTarget.id;
    const targetDevice = devices.find((device) => Number(device.id) === selectedTargetDeviceId);
    const config = {};
    if (methodType === 'wol') config.mac = normalizeMac(mac || deviceMac(targetDevice));
    if (methodType.startsWith('google_assistant_')) config.query = query.trim();
    if (methodType === 'android_tv_input') config.action = 'input';
    if (methodType === 'tv_input_then_player_home_cec') {
      const player = screen.players?.find((entry) => Number(entry.deviceId || entry.id) === selectedTargetDeviceId);
      const port = String(player?.expectedInput || '').match(/HDMI\s*([1-8])/i)?.[1];
      config.inputAction = `input_hdmi_${port}`;
      config.settleMs = 1000;
    }
    if (methodType === 'manual') config.instructions = instructions.trim();
    const id = `${phase}-${methodType}-${Date.now()}`;
    await updateMethods([...methods, {
      id, type: methodType, targetRole: role, targetDeviceId: selectedTargetDeviceId, config,
    }]);
    setMethodType('');
    setTargetDeviceId('');
    setMac('');
    setQuery('');
    setInstructions('');
  };

  const selectMethod = (type) => {
    const option = available.find((entry) => entry.type === type);
    const options = powerTargetOptions(screen, devices, type).filter((target) => !selectedDeviceId || tvMethods || target.id === Number(selectedDeviceId));
    const preferredId = preferredPowerTargetId(options, focusedDeviceId, option, screen);
    const target = options.find((entry) => entry.id === preferredId) || options[0] || null;
    setMacEdited(false);
    setMethodType(type);
    setTargetRole(target?.role || option?.role || 'television');
    setTargetDeviceId(target ? String(target.id) : '');
    setMac(type === 'wol' && target ? deviceMac(devices.find((device) => Number(device.id) === target.id)) : '');
  };

  const move = (index, offset) => {
    const destination = index + offset;
    if (destination < 0 || destination >= methods.length) return;
    const next = [...methods];
    [next[index], next[destination]] = [next[destination], next[index]];
    updateMethods(next);
  };

  const applyTelemetry = (result) => {
    const initial = result.telemetryBefore;
    const last = result.telemetryAfter;
    const baselineState = phase === 'startup' ? 'asleep' : 'awake';
    const desired = phase === 'startup' ? 'awake' : 'asleep';
    if (initial?.state === baselineState && last) {
      const field = tvMethods ? 'displayPower' : 'playerPower';
      setObservations((current) => ({ ...current, [field]: last.state === desired }));
      setObservationSource('live_telemetry');
      setObservationNotes(`Telemetry: ${initial.state} at ${initial.collectedAt}; ${last.state} at ${last.collectedAt}.`);
      setMessage(last.state === desired
        ? 'Power transition observed. Check that telemetry matches what you see, then save these results to confirm the method.'
        : 'No power transition observed. Check again when the device is ready, or record what you see.');
    } else {
      if (observationSource === 'live_telemetry') {
        setObservations((current) => ({ ...current, [tvMethods ? 'displayPower' : 'playerPower']: null }));
        setObservationNotes('Telemetry did not confirm a power transition.');
      }
      setMessage('Telemetry did not confirm a power transition. Leave the result unknown unless you can verify it independently.');
    }
  };

  const checkAgain = async () => {
    if (!probe || !selectedDeviceId) return;
    const generation = ++activeProbe.current;
    const stillActive = () => generation === activeProbe.current;
    setBusy(true);
    setMessage('Checking power telemetry again…');
    try {
      const snapshot = (await axios.get(`/api/areas/${areaId}/topology/screens/${screen.id}/power-observations`, { headers })).data;
      if (!stillActive()) return;
      const next = snapshot?.devices?.find((entry) => Number(entry.deviceId) === Number(selectedDeviceId));
      const result = { ...probe.result };
      // A late snapshot must still belong to the original single-command test.
      result.telemetryAfter = next && ['awake', 'asleep'].includes(next.state)
        && observationTime(next.collectedAt) >= observationTime(result.startedAt)
        && observationTime(next.collectedAt) > (observationTime(result.telemetryBefore?.collectedAt) || 0)
        ? next : null;
      setProbe({ ...probe, result });
      applyTelemetry(result);
    } catch (error) {
      if (stillActive()) setMessage(`Could not check telemetry: ${error.response?.data?.message || error.message}. Try Check again when telemetry is available.`);
    } finally {
      if (stillActive()) setBusy(false);
    }
  };

  const runProbe = async (method) => {
    const generation = ++activeProbe.current;
    const stillActive = () => generation === activeProbe.current;
    const requiredState = phase === 'startup' ? 'off' : 'on';
    const desired = phase === 'startup' ? 'awake' : 'asleep';
    const baselineState = phase === 'startup' ? 'asleep' : 'awake';
    const time = observationTime;
    const read = async () => (await axios.get(`/api/areas/${areaId}/topology/screens/${screen.id}/power-observations`, { headers })).data;
    let deliveredResult = null;
    setBusy(true);
    setMessage(`Testing only ${methodLabel(method.type, phase)}…`);
    setProbe(null);
    setObservations({ displayPower: null, playerPower: null, selectsInput: null, cecObserved: null });
    setObservationSource('physical');
    setObservationNotes('');
    try {
      const before = selectedDeviceId ? await read() : null;
      if (!stillActive()) return;
      const targetState = before?.devices?.find((entry) => Number(entry.deviceId) === powerMethodTargetId(method, screen))?.state;
      const confirmCurrentState = method.type === 'android_tv_power_toggle'
        ? targetState === baselineState || window.confirm(`Confirm the selected target is ${requiredState.toUpperCase()}. This test sends Remote Control Power exactly once.`)
        : false;
      if (method.type === 'android_tv_power_toggle' && !confirmCurrentState) { setMessage('Test cancelled.'); return; }
      const response = await axios.post(
        `/api/areas/${areaId}/topology/screens/${screen.id}/power-methods/test`,
        { phase, methodId: method.id, confirmCurrentState: confirmCurrentState ? requiredState : undefined },
        { headers }
      );
      if (!stillActive()) return;
      if ((selectedDeviceId && response.data?.accepted !== true) || response.data?.accepted === false || response.data?.commandAcknowledged === false || response.data?.packetEmitted === false) {
        throw new Error('Command delivery was not confirmed. No retry was sent.');
      }
      const result = { ...response.data };
      deliveredResult = result;
      if (selectedDeviceId) {
        const initial = before?.devices?.find((entry) => Number(entry.deviceId) === Number(selectedDeviceId));
        result.telemetryBefore = initial || null;
        const deadline = Date.now() + observationTimeoutMs;
        let last;
        setMessage('Command sent once. Watching power telemetry…');
        do {
          const snapshot = await read();
          if (!stillActive()) return;
          const next = snapshot?.devices?.find((entry) => Number(entry.deviceId) === Number(selectedDeviceId));
          if (next && next.state !== 'unknown' && time(next.collectedAt) >= time(result.startedAt)
            && time(next.collectedAt) > (time(initial?.collectedAt) || 0)) last = next;
          if (last?.state === desired || Date.now() >= deadline) break;
          await new Promise((resolve) => setTimeout(resolve, Math.min(1500, deadline - Date.now())));
          if (!stillActive()) return;
        } while (true);
        result.telemetryAfter = last || null;
        applyTelemetry(result);
      } else setMessage('Probe finished. Check the physical TV/player, then record what happened.');
      setProbe({ method, result });
    } catch (error) {
      if (!stillActive()) return;
      const result = deliveredResult || error.response?.data || { message: error.message };
      setProbe({ method, result, failed: !deliveredResult });
      setMessage(deliveredResult ? 'Command sent once, but telemetry could not be read. Use Check again when the device is ready.' : result.message || 'Power probe failed.');
    } finally {
      if (stillActive()) setBusy(false);
    }
  };

  const editProbeEvidence = (method) => {
    const evidence = method.verification?.evidence || {};
    const recorded = evidence.operatorObserved || {};
    setProbe({ method, result: evidence.probe || {}, editing: true });
    setObservations({
      displayPower: typeof recorded.displayPower === 'boolean' ? recorded.displayPower : null,
      playerPower: typeof recorded.playerPower === 'boolean' ? recorded.playerPower : null,
      selectsInput: typeof recorded.selectsInput === 'boolean' ? recorded.selectsInput : null,
      cecObserved: typeof recorded.cecObserved === 'boolean' ? recorded.cecObserved : null,
    });
    setObservationSource(evidence.observationSource || 'physical');
    setObservationNotes(evidence.notes || '');
    setMessage('Review the saved observations. No command has been sent.');
  };

  const confirmProbe = async (status) => {
    if (!probe) return;
    setBusy(true);
    try {
      const response = await axios.post(
        `/api/areas/${areaId}/topology/screens/${screen.id}/power-methods/confirm`,
        {
          phase,
          methodId: probe.method.id,
          status,
          evidence: {
            probe: probe.result,
            operatorObserved: observations,
            observationSource,
            notes: observationNotes.trim(),
          },
        },
        { headers }
      );
      onTopologyChange?.(response.data.topology || replaceProfile(response.data.powerProfile));
      setMessage(status === 'confirmed' ? 'Method confirmed and saved.' : 'Method marked as failed.');
      setProbe(null);
    } catch (error) {
      setMessage(error.response?.data?.message || error.message);
    } finally {
      setBusy(false);
    }
  };

  if (!screens.length) return null;
  const guidedSuggestions = phase === 'startup'
    ? [
      ['wol', 'Recommended: Wake-on-LAN'],
      ['android_tv_wake', 'Try a dedicated Wake key through Android TV Remote'],
      ['android_tv_power_toggle', 'Alternative: toggle power through Android TV Remote'],
      ['player_home_cec', 'Send Home to the player and see whether CEC wakes the TV'],
      ['tv_input_then_player_home_cec', 'Select the player HDMI input, then send Home'],
      ['google_assistant_on', 'Ask Google Assistant to turn the Screen on'],
    ]
    : [
      ['android_tv_standby', 'Try a dedicated Standby key through Android TV Remote'],
      ['android_tv_power_toggle', 'Alternative: toggle power through Android TV Remote'],
      ['google_assistant_off', 'Ask Google Assistant to turn the Screen off'],
      ['manual', 'Record a conventional remote or manual shutdown'],
    ];
  return (
    <section className="power-onboarding" aria-label="Power onboarding">
      <div className="power-onboarding__heading">
        <div><h3>{tvMethods ? 'TV power methods' : 'Power methods'}</h3>{!compact && <p>Prefer a tested wake-only method to start and a tested standby-only method to stop. Test each method on the selected device and save its observed effects.</p>}</div>
        {!selectedScreenId && <label>Screen / device<select value={`${screen?.id || ''}:${focusedDeviceId}`} onChange={(event) => {
          const [nextScreenId, nextDeviceId] = event.target.value.split(':');
          setScreenId(nextScreenId);
          setFocusedDeviceId(nextDeviceId);
        }}>{screens.map((entry) => <optgroup key={entry.id} label={entry.name}>{powerTargetOptions(entry, devices, '').map((target) => <option key={`${entry.id}:${target.id}`} value={`${entry.id}:${target.id}`}>{target.label}</option>)}</optgroup>)}</select></label>}
      </div>
      {!selectedPhase && <div className="power-onboarding__tabs">
        <button type="button" className={phase === 'startup' ? 'active' : ''} onClick={() => setPhase('startup')}>Startup</button>
        <button type="button" className={phase === 'shutdown' ? 'active' : ''} onClick={() => setPhase('shutdown')}>Shutdown</button>
      </div>}
      {!compact && <div className="power-onboarding__guide">
        <h4>{phase === 'startup' ? 'How can this Screen wake?' : 'How does this Screen shut down?'}</h4>
        <p>Test one method at a time. Confirm its effect before using it in the saved routine.</p>
        <div>
          {guidedSuggestions.map(([type, label]) => {
            const option = available.find((entry) => entry.type === type);
            const alreadyAdded = methods.some((method) => method.type === type);
            const configured = isPowerMethodConfigured(methods, type);
            return <button type="button" key={type} className={configured ? 'configured' : ''} disabled={!option || methods.length >= MAX_POWER_METHODS_PER_PHASE || busy} onClick={() => {
              selectMethod(type);
            }}>{configured ? 'Configured — add another: ' : alreadyAdded ? 'Add another: ' : 'Try: '}{label}</button>;
          })}
        </div>
      </div>
      }
      <div className="power-onboarding__methods">
        <p>{tvMethods ? 'Start/Stop uses the first enabled, confirmed TV method in this order. Test a method and save its results before running the routine.' : 'Saved preference order, not a sequence of commands. Saving a preference does not test it or enable automatic fallback after uncertain delivery.'}</p>
        {methods.some((method, index) => preferConfirmedPowerMethod(methods, phase)[index] !== method) &&
          <button type="button" disabled={busy} onClick={() => updateMethods(preferConfirmedPowerMethod(methods, phase))}>
            Use recommended tested method first
          </button>}
        {methods.map((method, index) => {
          if (!belongsToDevice(method)) return null;
          const methodTargetId = powerMethodTargetId(method, screen);
          const unsupported = method.type === 'native_app_standby'
            && nativeStandbyPermission(devices.find((entry) => Number(entry.id) === methodTargetId)) === 'unsupported';
          const methodTarget = powerTargetOptions(screen, devices, method.type)
            .find((target) => target.id === methodTargetId);
          return <div className="power-onboarding__method" key={method.id}>
            <span><strong>{index + 1}. {methodLabel(method.type, phase)}</strong><small>Command target: {methodTarget?.label || method.targetRole} · {unsupported ? 'Unsupported firmware' : method.verification?.status || 'untested'}</small><small>{powerEffectSummary(method, phase)}</small>{method.verification?.evidence?.observationSource && <small>Evidence: {String(method.verification.evidence.observationSource).replaceAll('_', ' ')}</small>}</span>
            <button type="button" disabled={busy || index === 0} onClick={() => move(index, -1)} aria-label="Move method earlier">↑</button>
            <button type="button" disabled={busy || index === methods.length - 1} onClick={() => move(index, 1)} aria-label="Move method later">↓</button>
            <button type="button" disabled={busy || unsupported} onClick={() => runProbe(method)}>{method.type === 'android_tv_power_toggle' ? 'Test once…' : 'Test'}</button>
            {method.verification?.status === 'confirmed' && <button type="button" disabled={busy || unsupported} onClick={() => editProbeEvidence(method)}>Edit evidence</button>}
            <button type="button" disabled={busy} onClick={() => updateMethods(methods.filter((entry) => entry.id !== method.id))}>Remove</button>
          </div>;
        })}
        {!methods.some(belongsToDevice) && <p>No {phase} methods configured.</p>}
      </div>
      {methods.length < MAX_POWER_METHODS_PER_PHASE && (
        <div className="power-onboarding__add">
          <label>Method<select value={methodType} onChange={(event) => {
            const next = event.target.value;
            selectMethod(next);
          }}><option value="">Add method…</option>{available.map((option) => <option key={option.type} value={option.type}>{option.label}</option>)}</select></label>
          {methodType && <label>Target device<select value={targetDeviceId} onChange={(event) => {
            const id = Number(event.target.value);
            const target = targetOptions.find((entry) => entry.id === id);
            setTargetDeviceId(event.target.value);
            setTargetRole(target?.role || 'television');
            setMacEdited(false);
            if (methodType === 'wol') setMac(deviceMac(devices.find((device) => Number(device.id) === id)));
          }}>{targetOptions.map((target) => <option key={target.id} value={target.id}>{target.label}</option>)}</select></label>}
          {methodType === 'wol' && <label>MAC address<input value={mac} onChange={(event) => { setMacEdited(true); setMac(event.target.value); }} placeholder="AA:BB:CC:DD:EE:FF" /></label>}
          {methodType.startsWith('google_assistant_') && <>
            <label>Assistant phrase<input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={phase === 'startup' ? 'Turn on Lobby TV' : 'Turn off Lobby TV'} /></label>
            <p>May target a Google-connected device or an Assistant-linked IR blaster. The receiver/blaster must retain power and connectivity. Assistant methods are currently manual-test only: bounded routine execution is not yet enabled. An IR power toggle is not a discrete on/off command.</p>
          </>}
          {methodType === 'manual' && <label>Instructions<input value={instructions} onChange={(event) => setInstructions(event.target.value)} placeholder="Use the original remote" /></label>}
          {methodType && <button type="button" disabled={busy || !targetOptions.some((target) => target.id === Number(targetDeviceId)) || (methodType === 'wol' && !normalizeMac(mac)) || (methodType.startsWith('google_assistant_') && !query.trim())} onClick={addMethod}>Add and save</button>}
          {methodType && available.find((option) => option.type === methodType)?.description && <p>{available.find((option) => option.type === methodType).description}</p>}
          {methodType && targetDeviceId && <p className="power-onboarding__target-confirmation">This will add <strong>{methodLabel(methodType, phase)}</strong> for <strong>{targetOptions.find((target) => String(target.id) === String(targetDeviceId))?.label}</strong>.</p>}
        </div>
      )}
      {probe && (
        <div className="power-onboarding__result">
          <h4>{probe.editing ? 'Revise recorded evidence' : 'What changed physically?'}</h4>
          <p>{probe.editing ? 'No command was sent. Update this record from preserved logs, telemetry, or corrected physical observations.' : phase === 'startup' ? 'Start with the Screen off. Record the final state after the single test.' : 'Start with the Screen and relevant players on. Record the final state after the single test.'}</p>
          <dl>
            <div><dt>Request accepted</dt><dd>{probe.result.accepted === true ? 'Yes' : 'No'}</dd></div>
            <div><dt>WOL packet emitted</dt><dd>{probe.result.packetEmitted == null ? 'Not applicable / unknown' : probe.result.packetEmitted ? 'Yes' : 'No'}</dd></div>
            <div><dt>Target reachable</dt><dd>{probe.result.targetReachable == null ? 'Unknown' : probe.result.targetReachable ? 'Yes' : 'No'}</dd></div>
            <div><dt>Display awake</dt><dd>{probe.result.displayAwake == null ? 'Needs your confirmation' : probe.result.displayAwake ? 'Yes' : 'No'}</dd></div>
            <div><dt>Player operational</dt><dd>{probe.result.playerOperational == null ? 'Needs your confirmation' : probe.result.playerOperational ? 'Yes' : 'No'}</dd></div>
            {selectedDeviceId && <div><dt>Latest power telemetry</dt><dd>{probe.result.telemetryAfter ? `${probe.result.telemetryAfter.state} (${probe.result.telemetryAfter.collectedAt})` : 'Unknown / awaiting fresh telemetry'}</dd></div>}
          </dl>
          {[
            ['displayPower', phase === 'startup' ? 'Did the TV/display turn on?' : 'Did the TV/display turn off?'],
            ['playerPower', phase === 'startup' ? 'Did the selected player wake?' : selectedDeviceId && !tvMethods ? 'Did this player enter standby?' : 'Did connected CEC player(s) also turn off?'],
            ['selectsInput', 'Did the TV select the correct player input?'],
            ['cecObserved', 'Was this change caused through HDMI-CEC?'],
          ].filter(([key]) => !selectedDeviceId || tvMethods || key === 'playerPower').map(([key, label]) => <label key={key}>{label}<select value={observations[key] == null ? '' : observations[key] ? 'yes' : 'no'} onChange={(event) => setObservations((current) => ({ ...current, [key]: event.target.value === '' ? null : event.target.value === 'yes' }))}><option value="">Not observed</option><option value="yes">Yes</option><option value="no">No</option></select></label>)}
          <label>Evidence source<select value={observationSource} onChange={(event) => setObservationSource(event.target.value)}><option value="physical">Physical observation</option><option value="live_telemetry">Live Suitestream telemetry</option><option value="device_logs">Preserved device logs</option><option value="mixed">Mixed evidence</option></select></label>
          <label>Evidence notes<input value={observationNotes} onChange={(event) => setObservationNotes(event.target.value)} placeholder="Test ID, setting state, log time, or physical observation" /></label>
          <div>
            <button type="button" disabled={busy || (selectedDeviceId ? observations[tvMethods ? 'displayPower' : 'playerPower'] !== true : observations.displayPower == null) || (selectedDeviceId && probe.failed)} onClick={() => confirmProbe('confirmed')}>
              {probe.editing ? 'Save corrected evidence' : 'Save these results'}
            </button>
            {!probe.editing && <button type="button" disabled={busy} onClick={() => confirmProbe('failed')}>Command failed</button>}
            {!probe.editing && selectedDeviceId && <button type="button" disabled={busy} onClick={checkAgain}>Check again</button>}
          </div>
        </div>
      )}
      {message && <p className="topology-setup__message" role="status">{message}</p>}
      <p className="power-onboarding__warning">Remote Control Power is a normal startup and shutdown method. During testing, confirm the target's current state first; Suitestream sends it once and never retries after uncertain delivery.</p>
    </section>
  );
}

export default PowerOnboarding;
